<script setup>
import { computed, onMounted, ref } from 'vue';
import {
    TeamOutlined,
    RiseOutlined,
    FallOutlined,
    FundOutlined,
    UserOutlined
} from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import { useAdminInfo } from '@/stores/useAdminInfo';
import { getDashboardStats } from '@/api/admin/dashboardStats';

const adminStore = useAdminInfo();
const loading = ref(true);
const username = ref('');
const stats = ref({
    consulted_users: 0,
    consultation_rounds: 0,
    net_asset_lift: 0,
    expense_reduction: 0,
    cash_flow_improvement: 0,
    scope: 'all'
});

const greeting = computed(() => {
    const h = new Date().getHours();
    if (h < 12) return '早安';
    if (h < 18) return '午安';
    return '晚安';
});

const formatCount = (v) => Number(v || 0).toLocaleString('zh-TW');
const formatSignedMoney = (v) => {
    const n = Number(v || 0);
    const abs = Math.abs(n).toLocaleString('zh-TW');
    if (n > 0) return `+${abs}`;
    if (n < 0) return `-${abs}`;
    return abs;
};

const cards = computed(() => [
    {
        key: 'users',
        label: '諮詢人數',
        value: formatCount(stats.value.consulted_users),
        unit: '人',
        hint:
            stats.value.consultation_rounds > 0
                ? `共 ${formatCount(stats.value.consultation_rounds)} 場已完成諮詢`
                : '以完成諮詢版為準',
        icon: TeamOutlined,
        tone: 'gold'
    },
    {
        key: 'nav',
        label: '提升淨資產',
        value: formatSignedMoney(stats.value.net_asset_lift),
        unit: '元',
        hint: '諮詢後淨資產較盤點版增加之合計',
        icon: RiseOutlined,
        tone: 'green'
    },
    {
        key: 'expense',
        label: '降低月支出',
        value: formatSignedMoney(stats.value.expense_reduction),
        unit: '元',
        hint: '諮詢後月支出較盤點版減少之合計',
        icon: FallOutlined,
        tone: 'orange'
    },
    {
        key: 'cashflow',
        label: '月現金流改善',
        value: formatSignedMoney(stats.value.cash_flow_improvement),
        unit: '元',
        hint: '諮詢後月現金流較盤點版改善之合計',
        icon: FundOutlined,
        tone: 'teal'
    }
]);

const loadStats = async () => {
    loading.value = true;
    try {
        const res = await getDashboardStats();
        if (res?.status !== 1) throw new Error(res?.message || '載入失敗');
        stats.value = {
            consulted_users: Number(res.data?.consulted_users || 0),
            consultation_rounds: Number(res.data?.consultation_rounds || 0),
            net_asset_lift: Number(res.data?.net_asset_lift || 0),
            expense_reduction: Number(res.data?.expense_reduction || 0),
            cash_flow_improvement: Number(res.data?.cash_flow_improvement || 0),
            scope: res.data?.scope || 'all'
        };
    } catch (e) {
        message.error(e.message || '儀表板統計載入失敗');
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
    const adminInfo = adminStore.getAdminInfo();
    username.value = adminInfo.name || adminInfo.email || '—';
    loadStats();
});
</script>

<template>
    <div class="dashboard-page">
        <header class="dashboard-hero">
            <div class="dashboard-hero__main">
                <a-avatar :size="52" class="dashboard-hero__avatar">
                    <template #icon>
                        <user-outlined />
                    </template>
                </a-avatar>
                <div class="min-w-0">
                    <h1 class="dashboard-hero__title">
                        {{ greeting }}，{{ username }}
                    </h1>
                    <p class="dashboard-hero__desc">
                        統計以已完成諮詢對照盤點版
                        <span v-if="stats.scope === 'consultant'">（僅您的學員）</span>
                    </p>
                </div>
            </div>
            <a-button :loading="loading" @click="loadStats">重新整理</a-button>
        </header>

        <a-spin :spinning="loading">
            <div class="dashboard-grid">
                <article
                    v-for="card in cards"
                    :key="card.key"
                    class="dashboard-card"
                    :class="`dashboard-card--${card.tone}`"
                >
                    <div class="dashboard-card__icon">
                        <component :is="card.icon" />
                    </div>
                    <div class="dashboard-card__body">
                        <div class="dashboard-card__label">{{ card.label }}</div>
                        <div class="dashboard-card__value">
                            <span class="dashboard-card__num">{{ card.value }}</span>
                            <span class="dashboard-card__unit">{{ card.unit }}</span>
                        </div>
                        <p class="dashboard-card__hint">{{ card.hint }}</p>
                    </div>
                </article>
            </div>
        </a-spin>
    </div>
</template>

<style scoped>
.dashboard-page {
    max-width: 1120px;
}

.dashboard-hero {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 18px;
    padding: 18px 20px;
    border-radius: 14px;
    border: 1px solid rgba(226, 232, 240, 0.95);
    background: linear-gradient(180deg, #fffdf8 0%, #ffffff 100%);
}

.dashboard-hero__main {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
}

.dashboard-hero__avatar {
    background: #c4a35a !important;
    flex-shrink: 0;
}

.dashboard-hero__title {
    margin: 0;
    font-size: 22px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.02em;
}

.dashboard-hero__desc {
    margin: 6px 0 0;
    font-size: 13px;
    color: #64748b;
}

.dashboard-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
}

.dashboard-card {
    display: flex;
    gap: 14px;
    padding: 18px 18px 16px;
    border-radius: 14px;
    border: 1px solid #e8edf0;
    background: #fff;
    box-shadow: 0 8px 20px -16px rgba(39, 43, 54, 0.28);
}

.dashboard-card__icon {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
}

.dashboard-card--gold .dashboard-card__icon {
    color: #8b6914;
    background: #f6f1e6;
}

.dashboard-card--green .dashboard-card__icon {
    color: #15803d;
    background: #dcfce7;
}

.dashboard-card--orange .dashboard-card__icon {
    color: #c2410c;
    background: #ffedd5;
}

.dashboard-card--teal .dashboard-card__icon {
    color: #0f766e;
    background: #ccfbf1;
}

.dashboard-card__label {
    font-size: 13px;
    font-weight: 700;
    color: #475569;
}

.dashboard-card__value {
    margin-top: 6px;
    display: flex;
    align-items: baseline;
    gap: 6px;
}

.dashboard-card__num {
    font-size: 28px;
    font-weight: 800;
    letter-spacing: -0.03em;
    color: #0f172a;
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
}

.dashboard-card__unit {
    font-size: 13px;
    color: #64748b;
    font-weight: 600;
}

.dashboard-card__hint {
    margin: 8px 0 0;
    font-size: 12px;
    line-height: 1.45;
    color: #94a3b8;
}

@media (max-width: 800px) {
    .dashboard-grid {
        grid-template-columns: 1fr;
    }

    .dashboard-hero {
        flex-direction: column;
    }

    .dashboard-card__num {
        font-size: 24px;
    }
}
</style>
